<template>
  <Layout>
    <FormulateForm
      ref="formRef"
      data-me="setting-notification-form"
      :form-errors="formErrors"
      @submit="handleSubmit"
    >
      <PageHeader
        data-me="setting-notification-form-page-header"
        :title="title.title"
        :subTitle="title.subTitle"
      >
        <template #afterTitle>
          <div class="d-flex justify-content-end">
            <McButton
              data-me="setting-notification-form-btn-cancel"
              class="ms-2"
              variant="secondary"
              icon="icon-outline_close"
              :text="$t('global.cancel')"
              @click="handleCancel"
            />

            <McButton
              v-if="$route.params.id"
              data-me="setting-notification-form-btn-change-history"
              class="ms-2"
              variant="secondary"
              icon="icon-outline_history"
              :text="$t('setting.list.notification.form.changeHistoryDetail')"
              @click="openChangeLog"
            />

            <McButton
              v-if="isHasAccess"
              data-me="setting-notification-form-btn-save"
              class="ms-2"
              variant="primary"
              icon="icon-outline_save"
              :text="$t('global.save')"
              type="submit"
              @click.prevent="handleFormSubmit(false)"
            />
          </div>
        </template>
      </PageHeader>

      <FormSkeleton v-if="isLoading" />

      <template v-else>
        <b-card>
          <h4>
            {{ $t('setting.list.notification.form.mainRule') }}
          </h4>
          <b-row>
            <b-col sm="6" md="3">
              <div class="mc-form-notification">
                <FormulateInput
                  id="name"
                  v-model="form.name"
                  data-me="setting-notification-form-input-rule-name"
                  name="ruleName"
                  :placeholder="$t('setting.list.notification.form.ruleName')"
                  help="required"
                  validation="required"
                  :validation-messages="validationMessages.ruleName"
                  :disabled="!isHasAccess"
                  validation-behavior="submit"
                  error-behavior="submit"
                >
                  <template #label>
                    <label for="name" class="d-flex justify-content-between align-items-center">
                      {{ $t('setting.list.notification.form.ruleName') }}
                      <i
                        v-b-tooltip.hover.bottomright
                        class="icon-outline_info"
                        :title="$t('setting.list.notification.tooltip')"
                      />
                    </label>
                  </template>
                </FormulateInput>
              </div>
            </b-col>
            <b-col sm="6" md="3">
              <div class="mc-form-notification">
                <FormulateInput
                  id="alertCode"
                  v-model="form.alertCode"
                  data-me="setting-notification-form-input-notification-name"
                  name="alertCode"
                  :label="$t('setting.list.notification.form.notificationName')"
                  type="value-help-v2"
                  :multiple="false"
                  :placeholder="`${$t('global.form.select')} ${$t('global.object.notification')}`"
                  :modalTitle="`${$t('global.form.select')} ${$t('global.object.notification')}`"
                  :listGetter="resourcefulQuery.notifications"
                  :tableFields="notificationFields"
                  help="required"
                  validation="^required"
                  :validation-messages="validationMessages.notificationName"
                  displayedBy="name"
                  trackBy="alertCode"
                  :disabled="!isAdding || !isHasAccess"
                  :fullObject="fullObject.notification"
                  validation-behavior="submit"
                  error-behavior="submit"
                  @update:fullObject="($event) => (fullObject.notification = $event)"
                >
                  <template #label>
                    <label
                      for="alertCode"
                      class="d-flex justify-content-between align-items-center"
                    >
                      {{ $t('setting.list.notification.form.notificationName') }}
                    </label>
                  </template>
                </FormulateInput>
              </div>
            </b-col>
            <b-col
              v-if="form.alertCode == 8 && hasAccess(['e4c-alert-overspeed-threshold:read'])"
              sm="6"
              md="3"
            >
              <div class="mc-form-notification__with-tooltip">
                <div ref="speedLimitWrapper">
                  <FormulateInput
                    id="speedLimit"
                    v-model="form.speedLimit"
                    data-me="setting-notification-form-input-speed-limit"
                    name="speedLimit"
                    help="required"
                    validation="required|number"
                    :validation-messages="{
                      required: `${$t('setting.list.tracker.speedLimit')} ${$t(
                        'global.validation.required'
                      )}`,
                      number: `${$t('setting.list.tracker.speedLimit')} ${$t(
                        'global.validation.number'
                      )}`,
                    }"
                    validation-behavior="submit"
                    error-behavior="submit"
                    suffix
                  >
                    <template #label>
                      <label
                        for="speedLimit"
                        class="d-flex justify-content-between align-items-center"
                      >
                        {{ $t('setting.list.notification.form.speedLimit') }}
                      </label>
                    </template>
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ `Km/${$t('global.time.hour')}` }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </div>
            </b-col>
            <b-col
              v-if="
                form.alertCode == 8 && hasAccess(['e4d-alert-overspeed-threshold-duration:read'])
              "
              sm="6"
              md="3"
            >
              <div class="mc-form-notification__with-tooltip">
                <div ref="durationWrapper">
                  <FormulateInput
                    id="duration"
                    v-model="form.duration"
                    data-me="setting-notification-form-input-speed-duration"
                    name="duration"
                    help="required"
                    type="select-resourceful-v2"
                    :optionsGetter="resourcefulQuery.durationOptions"
                    :optionLabelFn="({ item }) => item.text"
                    :label="$t('setting.list.notification.form.speedDuration')"
                    :placeholder="$t('setting.list.notification.form.speedDuration')"
                    validation="required"
                    :validation-messages="{
                      required: `${$t('setting.list.notification.form.speedDuration')} ${$t(
                        'global.validation.required'
                      )}`,
                    }"
                    trackBy="id"
                    validation-behavior="submit"
                    error-behavior="submit"
                    :fullObject="fullObject.duration"
                    @update:fullObject="
                      ($event) => {
                        fullObject.duration = $event
                        form.duration = $event ? $event.id : null
                      }
                    "
                  />
                </div>

                <b-tooltip
                  v-if="showTooltipDuration"
                  :target="() => $refs.durationWrapper"
                  placement="right"
                  triggers="manual"
                  :show="showTooltipDuration"
                >
                  {{ $t('setting.list.notification.form.durationTooltip') }}
                </b-tooltip>
              </div>
            </b-col>
          </b-row>

          <b-row v-if="form.alertCode == 7" class="my-4">
            <b-col md="4">
              <p class="text-muted">
                {{ $t('notification.text.chooseAlertCode') }}
              </p>
              <div v-b-tooltip.hover.topleft="$t('notification.text.tooltipInOutGeolocation')">
                <McRadioButton
                  id="allActivityGeofence"
                  key="allActivityGeofence"
                  v-model="alertSending"
                  data-me="setting-notification-form-radio-geofence-both"
                  name="alertSending"
                  :label="$t('notification.text.inOutGeolocation')"
                  initialValue="both"
                />
              </div>
              <div v-b-tooltip.hover.topleft="$t('notification.text.tooltipInGeolocation')">
                <McRadioButton
                  id="inActivityGeofence"
                  key="inActivityGeofence"
                  v-model="alertSending"
                  data-me="setting-notification-form-radio-geofence-in"
                  name="alertSending"
                  :label="$t('notification.text.inGeolocation')"
                  :initialValue="true"
                />
              </div>
              <div v-b-tooltip.hover.topleft="$t('notification.text.tooltipOutGeolocation')">
                <McRadioButton
                  id="outActivityGeofence"
                  key="outActivityGeofence"
                  v-model="alertSending"
                  data-me="setting-notification-form-radio-geofence-out"
                  name="alertSending"
                  :label="$t('notification.text.outGeolocation')"
                  :initialValue="false"
                />
              </div>
            </b-col>
          </b-row>

          <b-row v-if="form.alertCode == 12" class="my-4">
            <b-col md="4">
              <p class="text-muted">
                {{ $t('notification.text.chooseAlertCode') }}
              </p>
              <div v-b-tooltip.hover.topleft="$t('notification.text.tooltipOnOffMechine')">
                <McRadioButton
                  id="allActivityEngine"
                  key="allActivityEngine"
                  v-model="alertSending"
                  data-me="setting-notification-form-radio-engine-both"
                  name="alertSending"
                  :label="$t('notification.text.onOffMechine')"
                  initialValue="both"
                />
              </div>
              <div v-b-tooltip.hover.topleft="$t('notification.text.tooltipOnMechine')">
                <McRadioButton
                  id="onActivityEngine"
                  key="onActivityEngine"
                  v-model="alertSending"
                  data-me="setting-notification-form-radio-engine-on"
                  name="alertSending"
                  :label="$t('notification.text.onMechine')"
                  :initialValue="true"
                />
              </div>
              <div v-b-tooltip.hover.topleft="$t('notification.text.tooltipOffMechine')">
                <McRadioButton
                  id="offActivityEngine"
                  key="offActivityEngine"
                  v-model="alertSending"
                  data-me="setting-notification-form-radio-engine-off"
                  name="alertSending"
                  :label="$t('notification.text.offMechine')"
                  :initialValue="false"
                />
              </div>
            </b-col>
          </b-row>

          <b-row v-if="form.alertCode == 118" class="my-4">
            <b-col md="4">
              <p class="text-muted">
                {{ $t('notification.text.chooseAlertCode') }}
              </p>
              <div v-b-tooltip.hover.topleft="$t('notification.text.tooltipOnOffVehicle')">
                <McRadioButton
                  id="allActivityVehicle"
                  key="allActivityVehicle"
                  v-model="alertSending"
                  data-me="setting-notification-form-radio-vehicle-both"
                  name="alertSending"
                  :label="$t('notification.text.onOffVehicle')"
                  initialValue="both"
                />
              </div>
              <div v-b-tooltip.hover.topleft="$t('notification.text.tooltipOnVehicle')">
                <McRadioButton
                  id="onActivityVehicle"
                  key="onActivityVehicle"
                  v-model="alertSending"
                  data-me="setting-notification-form-radio-vehicle-on"
                  name="alertSending"
                  :label="$t('notification.text.onVehicle')"
                  :initialValue="true"
                />
              </div>
              <div v-b-tooltip.hover.topleft="$t('notification.text.tooltipOffVehicle')">
                <McRadioButton
                  id="offActivityVehicle"
                  key="offActivityVehicle"
                  v-model="alertSending"
                  data-me="setting-notification-form-radio-vehicle-off"
                  name="alertSending"
                  :label="$t('notification.text.offVehicle')"
                  :initialValue="false"
                />
              </div>
            </b-col>
          </b-row>

          <!-- Driving Time -->
          <b-row v-if="hasDrivingTimeAccess" class="my-4">
            <b-col md="3">
              <FormulateInput
                v-model="form.drivingTime"
                data-me="setting-notification-form-input-driving-time"
                name="drivingTime"
                type="date-picker"
                format="HH:mm"
                value-type="HH:mm"
                :label="$t('setting.list.notification.form.drivingTimeThreshold')"
                placeholder="00:00"
                datepickerType="time"
                :popup-class="[
                  'mc-driving-time-popup',
                  lang === 'id' && 'mc-driving-time-popup--id',
                ]"
                icon="icon-outline_clock"
                :clearable="false"
                help="required"
                validation="bail|required|drivingTimeRange"
                :validation-rules="{ drivingTimeRange }"
                :validation-messages="validationMessages.drivingTime"
                validation-behavior="submit"
                error-behavior="submit"
              />
            </b-col>
            <b-col md="3">
              <FormulateInput
                v-model="form.minimumRestTime"
                data-me="setting-notification-form-input-minimum-rest-time"
                name="minimumRestTime"
                type="number"
                :label="$t('setting.list.notification.form.minimumRestTime')"
                placeholder="10"
                suffix
                help="required"
                :validation="
                  bypassDrivingTimeValidation
                    ? 'bail|required|number'
                    : 'bail|required|number|min:1,value'
                "
                :validation-messages="validationMessages.minimumRestTime"
                validation-behavior="submit"
                error-behavior="submit"
              >
                <template #suffix>
                  <div class="formulate-input-suffix">
                    <span>{{ $t('global.time.minute') }}</span>
                  </div>
                </template>
              </FormulateInput>
            </b-col>
            <b-col md="3">
              <McToggle
                v-model="form.isRepeatNotification"
                data-me="setting-notification-form-toggle-repeat-notification"
                :label="$t('setting.list.notification.form.repeatNotificationEvery')"
                placement="left"
                compact
                class="mb-2"
              />

              <FormulateInput
                v-model="form.repeatNotificationInterval"
                data-me="setting-notification-form-input-repeat-notification-interval"
                name="repeatNotificationInterval"
                type="number"
                placeholder="60"
                suffix
                help="required"
                :validation="`bail|required|number${
                  bypassDrivingTimeValidation ? '' : '|min:30,value'
                }`"
                :validation-messages="validationMessages.repeatNotificationInterval"
                validation-behavior="submit"
                error-behavior="submit"
              >
                <template #suffix>
                  <div class="formulate-input-suffix">
                    <span>{{ $t('global.time.minute') }}</span>
                  </div>
                </template>
              </FormulateInput>
            </b-col>
            <b-col md="3" class="d-flex align-items-center">
              <i
                v-b-tooltip.hover.right.html
                class="icon-outline_info fs-4 text-muted"
                :title="notifyIntervalItems"
              />
            </b-col>
          </b-row>

          <span class="text-muted">
            {{ $t('setting.list.notification.form.urgencyLevel') }}
          </span>
          <b-row>
            <b-col md="5">
              <b-card
                data-me="setting-notification-form-action-urgency-important"
                :class="{
                  'mc-alert-rule-form__radio-card': true,
                  __active: form.urgencyLevel === 'Important',
                  __disabled: !isHasAccess,
                }"
                @click="isHasAccess ? (form.urgencyLevel = 'Important') : null"
              >
                <div>
                  <McRadioButton
                    id="important"
                    key="important"
                    v-model="form.urgencyLevel"
                    data-me="setting-notification-form-radio-urgency-important"
                    name="urgencyLevel"
                    initialValue="Important"
                    :disabled="!isHasAccess"
                    :label="$t('setting.list.notification.form.important')"
                  />
                  <span class="text-muted">
                    {{ $t('setting.list.notification.form.importantDesc') }}
                  </span>
                </div>
                <div v-if="!hasUrgentAlertScope">
                  <img
                    src="@/assets/images/notification/urgent.png"
                    width="120"
                    height="100"
                    alt="urgent"
                    class="float-end"
                  />
                </div>
                <div v-else>
                  <img
                    src="@/assets/images/notification/important.png"
                    alt="important"
                    width="120"
                    height="100"
                    class="float-end"
                  />
                </div>
              </b-card>
            </b-col>
            <b-col md="5">
              <b-card
                data-me="setting-notification-form-action-urgency-urgent"
                :class="{
                  'mc-alert-rule-form__radio-card': true,
                  __active: form.urgencyLevel === 'Urgent',
                  __disabled: !isHasAccess || !hasUrgentAlertScope,
                }"
                @click="isHasAccess && hasUrgentAlertScope ? (form.urgencyLevel = 'Urgent') : null"
              >
                <div>
                  <McRadioButton
                    id="urgent"
                    key="urgent"
                    v-model="form.urgencyLevel"
                    data-me="setting-notification-form-radio-urgency-urgent"
                    name="urgencyLevel"
                    :disabled="!isHasAccess || !hasUrgentAlertScope"
                    initialValue="Urgent"
                    :label="$t('setting.list.notification.form.urgent')"
                  />
                  <span class="text-muted">
                    {{ $t('notification.alert.urgentDesc') }}
                  </span>
                  <div v-if="!hasUrgentAlertScope" class="mt-2">
                    <McButton
                      v-b-modal.urgent-video-modal
                      data-me="setting-notification-form-btn-learn-more-urgent"
                      variant="outline-primary"
                      size="small"
                      icon="icon-outline_play-circle"
                      :text="$t('notification.alert.learnMoreVideo')"
                    />
                    <McButton
                      v-b-modal.urgent-active
                      data-me="setting-notification-form-btn-activate-urgent"
                      variant="primary"
                      size="small"
                      icon="icon-fill_checkmark-circle-2"
                      :text="$t('notification.alert.activateNow')"
                      class="ms-2"
                    />
                  </div>
                </div>
                <div v-if="hasUrgentAlertScope">
                  <img
                    src="@/assets/images/notification/urgent.png"
                    width="120"
                    height="100"
                    alt="urgent"
                    class="float-end"
                  />
                </div>

                <template v-if="form.urgencyLevel === 'Urgent'" #footer>
                  <i class="mc-alert__icon icon-fill_info"></i>
                  {{ $t('setting.list.notification.form.mobileWarning') }}
                  <a
                    data-me="setting-notification-form-link-mobile-app"
                    :href="'https://play.google.com/store/apps/details?id=com.mceasy.vsms&hl=id'"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Mobile App"
                  >
                    {{ $t('dashboard.scheduler.previewDaily.footer.inHere') }}
                  </a>
                </template>
              </b-card>
            </b-col>
          </b-row>
        </b-card>

        <b-card>
          <div
            class="mc-form-notification__accordion-header"
            data-me="setting-notification-form-action-toggle-additional-settings"
            @click="accordion1 = !accordion1"
          >
            <div>
              <h5>
                {{ $t('setting.list.notification.additionalSettings') }}
              </h5>
              <span v-if="!accordion1" class="text-muted">
                <span>
                  {{ $t('setting.list.notification.form.appliesTo') }}
                  <strong>{{
                    form.vehicleSelectionMode === 'selectedVehicles' && totalVehicles > 0
                      ? `${totalVehicles} ${$t('setting.list.notification.form.selectedVehicles')}`
                      : form.vehicleSelectionMode === 'vehicleGroup' && totalVgroups > 0
                      ? `${totalVgroups} ${$t('global.object.fleetGroup')}`
                      : $t('setting.list.notification.form.allVehicles')
                  }}</strong>
                </span>
                <span v-if="form.showHideIntensity">
                  &middot; {{ $t('setting.list.notification.form.intensity') }}
                  <strong>{{
                    ` ${form.alertCountThreshold}x ${$t('global.in')} ${form.alertCountWindow} ${$t(
                      'global.time.minutes'
                    )}`
                  }}</strong>
                </span>
                <span v-if="form.showHideGeolocation">
                  &middot; {{ $t('global.object.geolocation') }}
                  <strong>{{ `${form.geofences.length} ${$t('global.field.address')}` }}</strong>
                </span>
                <span v-if="form.showHideTime">
                  &middot; {{ $t('global.time.time') }}
                  <strong>{{ $t('setting.list.notification.form.adjusted') }}</strong>
                </span>
              </span>
            </div>
            <i :class="['icon-outline_chevron-' + (accordion1 ? 'up' : 'down')]"></i>
          </div>
          <b-collapse id="collapse1" v-model="accordion1" class="mt-3">
            <b-row>
              <div class="mc-form-notification__additional-settings__vehicles-selection">
                <b-col md="3">
                  <span>
                    {{ $t('setting.list.notification.form.appliesTo') }}
                    {{ $t('setting.homePage.kendaraan') }}
                  </span>
                </b-col>
                <b-col md="9">
                  <div>
                    <McRadioButton
                      id="allVehicles"
                      key="allVehicles"
                      v-model="form.vehicleSelectionMode"
                      data-me="setting-notification-form-radio-vehicles-all"
                      :label="$t('setting.list.notification.form.allVehicles')"
                      initialValue="allVehicles"
                      @change="setVehicleObj('allVehicles')"
                    />
                    <b-row
                      :class="
                        form.vehicleSelectionMode == 'vehicleGroup'
                          ? ''
                          : 'd-flex align-items-center'
                      "
                    >
                      <b-col
                        md="3"
                        :class="form.vehicleSelectionMode == 'vehicleGroup' ? 'mt-2' : ''"
                      >
                        <McRadioButton
                          id="vehicleGroup"
                          key="vehicleGroup"
                          v-model="form.vehicleSelectionMode"
                          data-me="setting-notification-form-radio-vehicles-group"
                          :label="$t('setting.list.notification.form.selectedVgroup')"
                          initialValue="vehicleGroup"
                          :disabled="checkAlertCategory"
                          @change="setVehicleObj('vehicleGroup')"
                        />
                      </b-col>
                      <b-col md="9" class="mc-form-notification mb-2">
                        <FormulateInput
                          id="vGroups"
                          v-model="form.vgroups"
                          data-me="setting-notification-form-input-vehicle-group"
                          type="value-help-v2"
                          :placeholder="`${$t('global.form.select')} ${$t(
                            'global.object.fleetGroup'
                          )}`"
                          :modalTitle="`${$t('global.form.select')} ${$t(
                            'global.object.fleetGroup'
                          )}`"
                          :listGetter="resourcefulQuery.vGroups"
                          :tableFields="vGroupFields"
                          :validation="
                            form.vehicleSelectionMode == 'vehicleGroup' ? 'required' : ''
                          "
                          :help="form.vehicleSelectionMode == 'vehicleGroup' ? 'required' : ''"
                          :validation-messages="{
                            required: `${$t('global.object.fleetGroup')} ${$t(
                              'global.validation.required'
                            )}`,
                          }"
                          :disabled="!isHasAccess || form.vehicleSelectionMode !== 'vehicleGroup'"
                          displayedBy="name"
                          trackBy="id"
                          :multiple="true"
                          :fullObject="fullObject.vgroup"
                          validation-behavior="blur"
                          :hasDisabledItem="hasInaccessibleVgroup"
                          :disabledItem="$t('global.object.anotherGroup')"
                          error-behavior="blur"
                          @update:fullObject="($event) => (fullObject.vgroup = $event)"
                        />
                      </b-col>
                    </b-row>
                    <b-row
                      :class="
                        form.vehicleSelectionMode == 'selectedVehicles'
                          ? ''
                          : 'd-flex align-items-center'
                      "
                    >
                      <b-col
                        md="3"
                        :class="form.vehicleSelectionMode == 'selectedVehicles' ? 'mt-2' : ''"
                      >
                        <McRadioButton
                          id="selectedVehicles"
                          key="selectedVehicles"
                          v-model="form.vehicleSelectionMode"
                          data-me="setting-notification-form-radio-vehicles-selected"
                          :label="$t('setting.list.notification.form.selectedVehicles')"
                          initialValue="selectedVehicles"
                          :disabled="checkAlertCategory"
                          @change="setVehicleObj('selectedVehicles')"
                        />
                      </b-col>
                      <b-col md="9" class="mc-form-notification mb-2">
                        <FormulateInput
                          id="vehicles"
                          v-model="form.vehicles"
                          data-me="setting-notification-form-input-vehicles"
                          name="vehicles"
                          type="report-filter-value-help"
                          :multiple="true"
                          :placeholder="`${$t('global.form.select')} ${$t(
                            'global.object.licensePlate'
                          )}`"
                          :modalTitle="`${$t('global.form.select')} ${$t('master.kendaraan')}`"
                          :listGetter="(filter) => resourcefulQuery.vehicles(filter)"
                          :tableFields="vehicleFields"
                          displayedBy="licensePlate"
                          trackBy="id"
                          :filters="vehicleFilters"
                          :fullObject="fullObject.licensePlate"
                          :validation="
                            form.vehicleSelectionMode == 'selectedVehicles' ? 'required' : ''
                          "
                          :help="form.vehicleSelectionMode == 'selectedVehicles' ? 'required' : ''"
                          validation-behavior="submit"
                          error-behavior="submit"
                          :disabled="
                            !isHasAccess ||
                            form.vehicleSelectionMode !== 'selectedVehicles' ||
                            checkAlertCategory
                          "
                          :hasDisabledItem="hasInaccessibleVehicle"
                          :disabledItem="$t('global.object.anotherGroup')"
                          @update:fullObject="($event) => (fullObject.licensePlate = $event)"
                        />
                      </b-col>
                    </b-row>
                  </div>
                </b-col>
              </div>
            </b-row>
            <template v-if="!hasDrivingTimeAccess">
              <b-col md="12">
                <McToggle
                  v-model="form.showHideIntensity"
                  data-me="setting-notification-form-toggle-intensity"
                  :label="$t('setting.list.notification.form.adjustIntensity')"
                  placement="left"
                  class="mx-1 mb-1"
                  :disabled="!isHasAccess || checkAlertCategory"
                  @change="onChangeToggleIntensity"
                />

                <i v-b-tooltip.hover.right.html class="icon-outline_info" :title="tooltipContent" />

                <div v-show="form.showHideIntensity" class="mc-border-intensity ms-1">
                  {{ $t('setting.list.notification.form.intensityText1') }}
                  <FormulateInput
                    id="intensity"
                    v-model="form.alertCountThreshold"
                    data-me="setting-notification-form-input-intensity-threshold"
                    name="alertCountThreshold"
                    type="number"
                    :validation="
                      form.showHideIntensity
                        ? 'bail|required|number|max:100|between:1,101,value|maxDigits:3'
                        : ''
                    "
                    :validation-messages="{
                      max: 'Max 100',
                      maxDigits: 'Max 3 digits',
                      between: '2-100',
                    }"
                    validation-behavior="submit"
                    error-behavior="submit"
                    suffix
                    class="mc-form-intensity"
                  >
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ $t('global.times') }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                  {{ $t('setting.list.notification.form.intensityText2') }}
                  <FormulateInput
                    id="intensityRange"
                    v-model="form.alertCountWindow"
                    data-me="setting-notification-form-input-intensity-window"
                    name="alertCountWindow"
                    type="number"
                    :validation="`${
                      form.showHideIntensity
                        ? 'bail|required|number|max:600|between:0,601,value|maxDigits:3'
                        : ''
                    }`"
                    :validation-messages="{
                      max: 'Max 600',
                      maxDigits: 'Max 3 digits',
                      between: '1-600',
                    }"
                    validation-behavior="submit"
                    error-behavior="submit"
                    class="mc-form-intensity-range"
                    suffix
                  >
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ $t('global.time.minutes') }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
              <b-col md="12">
                <McToggle
                  v-model="form.showHideGeolocation"
                  data-me="setting-notification-form-toggle-geolocation"
                  :label="$t('setting.list.notification.form.basedOnGeofence')"
                  placement="left"
                  class="ms-1 mt-2"
                  :disabled="!isHasAccess || checkAlertCategory"
                  @change="onChangeToggleGeofence"
                />
                <div v-if="form.showHideGeolocation" class="ms-1">
                  <McRadioButton
                    v-if="form.alertCode != 7"
                    id="inside"
                    key="inside"
                    v-model="form.geofenceType"
                    data-me="setting-notification-form-radio-geofence-inside"
                    initialValue="inside"
                    @change="setGeofenceObj('inside')"
                  >
                    <template #label>
                      <label for="inside" class="form-check-label">
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <span v-html="$t('notification.setting.emailNotification.insideGeo')" />
                      </label>
                    </template>
                  </McRadioButton>
                  <div :class="form.alertCode != 7 ? 'ms-3' : 'mc-geofence-alignment'">
                    <FormulateInput
                      v-model="form.insideGeofenceId"
                      data-me="setting-notification-form-input-geofence-inside"
                      type="geofence-value-help"
                      name="insideGeofence"
                      trackBy="geofenceId"
                      :placeholder="`${$t('global.form.select')} ${$t(
                        'global.object.geolocation'
                      )}`"
                      displayedBy="fenceName"
                      :fullObject="insideObj"
                      :listGetter="queryGeofence.get"
                      :multiple="true"
                      :tableFields="fieldGeofence"
                      :modalTitle="`${$t('global.form.select')} ${$t('global.object.geolocation')}`"
                      :disabled="form.geofenceType !== 'inside' || !isHasAccess"
                      :geofenceAllData="geofenceAllData"
                      :help="
                        form.showHideGeolocation && form.geofenceType == 'inside' ? 'required' : ''
                      "
                      :validation="
                        form.showHideGeolocation && form.geofenceType == 'inside' ? 'required' : ''
                      "
                      :validation-messages="validationMessages.geolocation"
                      validation-behavior="submit"
                      error-behavior="submit"
                      @input:exception="handleException"
                      @update:fullObject="insideObj = $event"
                    />
                  </div>
                  <McRadioButton
                    v-if="form.alertCode != 7"
                    id="outside"
                    key="outside"
                    v-model="form.geofenceType"
                    data-me="setting-notification-form-radio-geofence-outside"
                    initialValue="outside"
                    @change="setGeofenceObj('outside')"
                  >
                    <template #label>
                      <label for="outside" class="form-check-label">
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <span v-html="$t('notification.setting.emailNotification.outsideGeo')" />
                      </label>
                    </template>
                  </McRadioButton>
                  <div class="ms-3">
                    <FormulateInput
                      v-if="form.alertCode != 7"
                      v-model="form.outsideGeofenceId"
                      data-me="setting-notification-form-input-geofence-outside"
                      type="geofence-value-help"
                      name="outsideGeofence"
                      trackBy="geofenceId"
                      :placeholder="`${$t('global.form.select')} ${$t(
                        'global.object.geolocation'
                      )}`"
                      displayedBy="fenceName"
                      :fullObject="outsideObj"
                      :listGetter="queryGeofence.get"
                      :multiple="true"
                      :tableFields="fieldGeofence"
                      :modalTitle="`${$t('global.form.select')} ${$t('global.object.geolocation')}`"
                      :disabled="form.geofenceType !== 'outside' || !isHasAccess"
                      :geofenceAllData="geofenceAllData"
                      :help="
                        form.showHideGeolocation && form.geofenceType == 'outside' ? 'required' : ''
                      "
                      :validation="
                        form.showHideGeolocation && form.geofenceType == 'outside' ? 'required' : ''
                      "
                      :validation-messages="validationMessages.geolocation"
                      validation-behavior="submit"
                      error-behavior="submit"
                      @input:exception="handleException"
                      @update:fullObject="outsideObj = $event"
                    />
                  </div>
                </div>
              </b-col>
              <b-col md="12">
                <div class="mc-form-notification mt-3">
                  <McToggle
                    v-model="form.showHideTime"
                    data-me="setting-notification-form-toggle-time"
                    :label="$t('setting.list.notification.form.basedOnDayTime')"
                    placement="left"
                    class="ms-1"
                    :disabled="!isHasAccess || checkAlertCategory"
                    @change="onChangeToggleTime"
                  />
                  <div v-show="form.showHideTime" class="ms-1">
                    <FormulateInput
                      id="timeRange"
                      v-model="form.timeRange"
                      data-me="setting-notification-form-input-time-range"
                      type="date-picker"
                      name="timeRange"
                      format="HH:mm"
                      datepickerType="time"
                      value-type="HH:mm:ss"
                      range
                      clearable
                      icon="icon-outline_clock"
                      :placeholder="`${$t('global.form.select')} ${$t('global.time.range')}`"
                      :help="form.showHideTime ? 'required' : ''"
                      :validation="form.showHideTime ? 'required' : ''"
                      validation-behavior="submit"
                      error-behavior="submit"
                      :validation-messages="validationMessages.timeRange"
                    >
                      <template #label>
                        <label
                          for="timeRange"
                          class="d-flex justify-content-between align-items-center text-wrap"
                        >
                          {{ $t('setting.list.notification.form.effectiveTime') }}
                        </label>
                      </template>
                    </FormulateInput>

                    <b-form-group class="mc-day-checkbox">
                      <div class="d-flex">
                        <div
                          v-for="option in dayOptions"
                          :key="option.value"
                          class="mc-checkbox-options me-3"
                        >
                          <b-form-checkbox
                            v-model="selectedDays"
                            :data-me="`setting-notification-form-checkbox-day-${option.value}`"
                            :value="option.value"
                            plain
                            size="sm"
                            :disabled="option.disabled || !isHasAccess"
                          />
                          <span class="mt-2">{{ option.text }}</span>
                        </div>
                      </div>
                    </b-form-group>
                  </div>
                </div>
              </b-col>
            </template>
          </b-collapse>
        </b-card>
        <b-card>
          <div
            class="mc-form-notification__accordion-header"
            data-me="setting-notification-form-action-toggle-notification-delivery"
            @click="accordion2 = !accordion2"
          >
            <div>
              <h5>{{ $t('setting.list.notification.form.notificationDelivery') }}</h5>
              <span v-if="!accordion2" class="text-muted">
                <span>
                  {{ 'Platform McEasy' }}
                  <strong>{{
                    form.users.length > 0
                      ? `${form.users.length} ${$t('setting.list.notification.form.certainUser')}`
                      : $t('setting.list.notification.form.allUsers')
                  }}</strong>
                </span>

                <span v-if="form.contactLists.length > 0">
                  &middot;
                  <strong>{{
                    `WhatsApp & Email ${form.contactLists.length} ${$t('global.object.contact')}`
                  }}</strong>
                </span>
              </span>
            </div>
            <i :class="['icon-outline_chevron-' + (accordion2 ? 'up' : 'down')]"></i>
          </div>
          <b-collapse v-model="accordion2">
            <b-row>
              <b-col md="3">
                <span> {{ 'Platform McEasy' }} </span>
              </b-col>
              <b-col md="9">
                <McRadioButton
                  id="allUsers"
                  key="allUsers"
                  v-model="form.userSettings"
                  data-me="setting-notification-form-radio-users-all"
                  initialValue="all"
                  :label="$t('setting.list.notification.form.allUsers')"
                  :disabled="!isHasAccess"
                  @change="setUserSettings('all')"
                />

                <b-row class="mb-3 flex-nowrap">
                  <b-col
                    md="3"
                    class="align-content-center"
                    :class="form.userSettings === 'selected' ? 'mb-4' : ''"
                  >
                    <McRadioButton
                      id="selectedUsers"
                      key="selectedUsers"
                      v-model="form.userSettings"
                      data-me="setting-notification-form-radio-users-selected"
                      initialValue="selected"
                      :label="$t('setting.list.notification.form.certainUser')"
                      :disabled="!isHasAccess"
                      @change="setUserSettings('selected')"
                    />
                  </b-col>
                  <b-col md="9">
                    <FormulateInput
                      id="userSettings"
                      v-model="form.users"
                      data-me="setting-notification-form-input-users"
                      name="userSettings"
                      type="report-filter-value-help"
                      :multiple="true"
                      :placeholder="`${$t('global.form.select')} ${$t(
                        'setting.list.notification.form.users'
                      )}`"
                      :modalTitle="`${$t('global.form.select')} ${$t(
                        'setting.list.notification.form.users'
                      )}`"
                      :disabled="form.userSettings !== 'selected' || !isHasAccess"
                      :listGetter="resourcefulQuery.users"
                      :tableFields="userFields"
                      :filters="userFilters"
                      :help="form.userSettings === 'selected' ? 'required' : ''"
                      :validation="form.userSettings === 'selected' ? 'required' : ''"
                      displayedBy="fullname"
                      validation-behavior="submit"
                      error-behavior="submit"
                      trackBy="id"
                      :fullObject="fullObject.users"
                      @update:fullObject="($event) => (fullObject.users = $event)"
                    />
                  </b-col>
                </b-row>
              </b-col>
            </b-row>

            <hr />
            <b-row>
              <b-col md="3">
                <span> {{ 'WhatsApp & Email' }} </span>
              </b-col>
              <b-col md="9">
                <div data-me="setting-notification-form-contact-list">
                  <ContactList
                    :isAdding="isAdding"
                    @addContact="addContact"
                    @updateData="updateData"
                    @deleteData="deleteData"
                  />
                </div>
              </b-col>
            </b-row>
          </b-collapse>
        </b-card>
        <b-card>
          <div
            class="mc-form-notification__accordion-header"
            data-me="setting-notification-form-action-toggle-automated-action"
            @click="accordion3 = !accordion3"
          >
            <div>
              <h5>{{ $t('setting.list.notification.form.automatedAction') }}</h5>
              <span v-if="!accordion3" class="text-muted">
                {{ $t('setting.list.notification.form.openPopupAutomated') }}
                <strong>{{ form.autoPopup ? $t('global.yes') : $t('global.no') }}</strong>
              </span>
            </div>
            <i :class="['icon-outline_chevron-' + (accordion3 ? 'up' : 'down')]"></i>
          </div>
          <b-collapse v-model="accordion3">
            <McToggle
              v-model="form.autoPopup"
              data-me="setting-notification-form-toggle-auto-popup"
              :label="$t('setting.list.notification.form.popupOpenAutomatically')"
              :disabled="!isHasAccess"
              class="my-1"
              @change="onChangeAutoPopup"
            />
          </b-collapse>
        </b-card>
      </template>
    </FormulateForm>
    <b-modal
      id="modal-confirm-override"
      data-me="setting-notification-form-modal-override"
      :title="`${$t('setting.list.notification.form.overrideTitle')} ${overrideModal.field}`"
      centered
      size="md"
    >
      <i18n path="setting.list.notification.form.overrideDescription" tag="span">
        <strong>{{ overrideModal.field }}</strong>
        <strong>{{ overrideModal.rule }}</strong>
      </i18n>
      <div class="d-flex align-items-center gap-2 py-2">
        <span class="text-muted">{{ `${overrideModal.oldValue} ${overrideModal.unit}` }}</span>
        <i class="mc-fa icon-outline_arrow-right"></i>
        <span>
          <strong>{{ `${overrideModal.newValue} ${overrideModal.unit}` }}</strong>
        </span>
      </div>
      <span class="mt-2">{{ $t('global.continueSave') }}</span>
      <template #modal-footer="{ cancel }">
        <McButton
          data-me="setting-notification-form-modal-btn-cancel"
          variant="secondary"
          class="me-2"
          :text="$t('global.action.cancel')"
          @click="cancel()"
        />
        <McButton
          data-me="setting-notification-form-modal-btn-save"
          :text="`${$t('global.action.yes')}, ${$t('global.action.save')}`"
          @click="handleFormSubmit(true)"
        />
      </template>
    </b-modal>

    <div data-me="setting-notification-form-change-log">
      <ChangeLogDetail ref="changelogModal" :alert-rule-id="$route.params.id" />
    </div>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import { mapGetters } from 'vuex'
import PageHeader from '@/components/page-header-v2.vue'
import McButton from '@/components/McButton'
import i18n from '../../../../i18n'
import McToggle from '@/components/McToggle'
import { GET_ADDRESS } from '@/services/api/master/address'
import McRadioButton from '@/components/McRadioButton'
import { GET_VEHICLE_FILTER, GET_VEHICLE_FILTER_ALL } from '@/services/api/master/fleet'
import ContactList from './components/ContactList.vue'
import {
  GET_ALERT_CODES_PAGINATED,
  GET_NOTIFICATIONS_LIST_BY_ID,
  CREATE_NOTIFICATION_RULES,
  UPDATE_NOTIFICATION_RULES,
  GET_RULES_DURATION,
} from '@/services/api/setting/notification/List'
import { GET_EMPLOYEES_PAGINATED, GET_EMPLOYEES } from '@/services/api/master/employees'
import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'
import { Helper } from '@/mixin/helper'
import { GET_VGROUPS_PAGINATED } from '@/services/api/vehicle/vgroup'
import ChangeLogDetail from './components/ChangeLogDetail.vue'
import FormSkeleton from './components/FormSkeleton.vue'

export default {
  page: {
    title: 'Setting Notification',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    McButton,
    McRadioButton,
    McToggle,
    ContactList,
    ChangeLogDetail,
    FormSkeleton,
  },
  mixins: [Helper],

  apollo: {
    vehicles: {
      query: GET_VEHICLE_FILTER_ALL,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            status: ['I', 'R', 'A'],
            parentOnly: true,
            orderBy: JSON.stringify({ licensePlate: 'asc' }),
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        let res = { ...data.vehicles }
        let vehicleTypes = res.vehicles.map((item) => {
          return {
            vehicleType: item.vehicleType
              ? [
                  item.vehicleType.manufacturer ?? '-',
                  item.vehicleType.model ?? '-',
                  item.vehicleType.type ?? '-',
                ].join(' / ')
              : '-',
          }
        })
        this.vehicleTypes = [...new Set(vehicleTypes.map((item) => item.vehicleType))].map(
          (item) => {
            return {
              id: item,
              name: item,
            }
          }
        )
      },
    },
    alertCodesPaginated: {
      query: GET_ALERT_CODES_PAGINATED,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            pagination: {
              page: 1,
              show: 9999,
            },
            isEnabledInRule: true,
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          const alertCodes = data.alertCodesPaginated.alertCodesPaginated.data

          const alertCodesQA = alertCodes.filter(
            (item) =>
              Array.isArray(item.alertCategories) &&
              item.alertCategories.some((cat) => cat.alertCategoryId === 6)
          )

          const alertCodesNonQA = alertCodes.filter(
            (item) =>
              !(
                Array.isArray(item.alertCategories) &&
                item.alertCategories.some((cat) => cat.alertCategoryId === 6)
              )
          )

          this.alertCodes = [...alertCodesQA, ...alertCodesNonQA]
        }
      },
    },
    geofences: {
      query: GET_ADDRESS,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            pagination: {
              page: 1,
              show: 9999,
            },
            ownership: ['Customer', 'Own'],
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          this.geofenceAllData = data.geofences.geofences.data
        }
      },
    },
    employees: {
      query: GET_EMPLOYEES,
      fetchPolicy: 'no-cache',
      result({ data, loading }) {
        if (!loading) {
          this.employeesAllData = data.employees.employees
        }
      },
    },
    ugroups: {
      query: GET_USER_GROUP,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            pagination: {
              page: 1,
              show: 9999,
            },
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          this.uGroups = data.ugroups.ugroups.data.map((item) => {
            return {
              id: item.id,
              name: item.name,
            }
          })
        }
      },
    },
    alertRules: {
      query: GET_RULES_DURATION,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            alertCode: 8,
            withMinThresholdTime: true,
            pagination: {
              page: 1,
              show: 5,
            },
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        const duration = data?.alertRules?.alertRules?.data?.[0]?.minThresholdTime ?? 0
        const matchId = this.durationOptions?.find((item) => item.value === duration)?.id ?? 0
        this.form.duration = matchId
        this.fullObject.duration = this.durationOptions?.find((item) => item.value === duration)
        this.predefinedDuration = this.form.duration
      },
    },
  },

  data() {
    return {
      fullObject: {
        notification: {},
        licensePlate: {},
        users: {},
        duration: {},
        vgroup: {},
      },
      predefinedDuration: 0,
      predefinedMinRestTime: 0,
      overrideType: null,
      form: {
        alertCode: null,
        showHideGeolocation: false,
        geofenceType: 'inside',
        insideGeofenceId: [],
        outsideGeofenceId: [],
        showHideTime: false,
        timeRange: [],
        contactLists: [],
        vehicles: [],
        name: '',
        speedLimit: null,
        showHideIntensity: false,
        alertCountThreshold: 3,
        alertCountWindow: 20,
        users: [],
        userSettings: 'all',
        urgencyLevel: 'Important',
        autoPopup: false,
        vehicleSelectionMode: 'allVehicles',
        geofences: [],
        duration: null,
        vgroups: [],
        alertFlag: null,
        drivingTime: '04:00', // Default value driving time is 4 hours
        minimumRestTime: 30, // Default value minimum rest time is 30 minutes
        repeatNotificationInterval: 60, // Default value repeat notification interval is 60 minutes
        isRepeatNotification: true,
      },
      insideObj: [],
      outsideObj: [],
      geofenceAllData: [],
      employeesAllData: [],
      geofenceSelectionMode: 'Selection',
      alertSending: 'both',
      exception: false,
      selectedDays: [0, 1, 2, 3, 4, 5, 6], // Must be an array reference!
      vehicleTypes: [],
      vGroups: [],
      isAdding: true,
      isLoading: false,
      lang: i18n.locale,
      hasInaccessibleVehicle: false,
      hasInaccessibleVgroup: false,
      totalVehicles: 0,
      totalVgroups: 0,
      isTheUserInList: false,
      alertCodes: [],
      uGroups: [],
      formErrors: [],
      accordion1: false,
      accordion2: false,
      accordion3: false,
      showTooltip: false,
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),

    title() {
      return this.isAdding
        ? {
            title: this.$t('setting.list.notification.add'),
            subTitle: this.$t('setting.list.notification.addDesc'),
          }
        : {
            title: this.$t('setting.list.notification.detail'),
            subTitle: this.$t('setting.list.notification.detailDesc'),
          }
    },

    isHasAccess() {
      return this.hasAccessAll([
        'e4b-alert-center-rule-setting:update',
        'e4b-alert-center-rule-setting:create',
      ])
    },

    hasUrgentAlertScope() {
      return this.hasAccess('e4e-alert-center-urgent:read')
    },
    hasDrivingTimeAccess() {
      return this.form.alertCode == 117 && this.hasAccess('e4k-alert-driving-time-threshold:read')
    },
    bypassDrivingTimeValidation() {
      return this.getFeatureFlagValue('bypassDrivingTimeValidation')
    },

    overrideModal() {
      const isDrivingTime = this.overrideType === 'drivingTime'

      const alertName = this.fullObject.notification ?? {}

      return {
        field: this.$t(
          isDrivingTime
            ? 'setting.list.notification.form.minimumRestTime'
            : 'setting.list.notification.form.speedDuration'
        ),
        rule: this.lang == 'en' ? alertName.descriptionEng : alertName.descriptionIdn,
        oldValue: isDrivingTime ? this.predefinedMinRestTime : this.predefinedDuration,
        newValue: isDrivingTime ? this.form.minimumRestTime : this.form.duration,
        unit: this.$t(isDrivingTime ? 'global.time.minute' : 'global.time.seconds'),
      }
    },

    resourcefulQuery() {
      return {
        notifications: async (opts) => {
          const alerts = this.alertCodes

          let data = alerts.map((item) => {
            return {
              ...item,
              name: this.lang == 'en' ? item.descriptionEng : item.descriptionIdn,
              description: this.lang == 'en' ? item.fullDescriptionEng : item.fullDescriptionIdn,
            }
          })

          if (opts.search) {
            const name = this.lang == 'en' ? 'descriptionEng' : 'descriptionIdn'
            const desc = this.lang == 'en' ? 'fullDescriptionEng' : 'fullDescriptionIdn'
            data = data.filter(
              (item) =>
                this.isContains(opts.search, item[name]) || this.isContains(opts.search, item[desc])
            )
          }

          // Manual pagination
          const page = opts.page || 1
          const limit = opts.limit || 10
          const start = (page - 1) * limit
          const end = start + limit
          const paginatedData = data.slice(start, end)

          return {
            metadata: {
              total_count: data.length,
              page,
            },
            data: {
              paginated_result: paginatedData,
            },
          }
        },
        vehicles: async (opts) => {
          const { orderBy, ...otherOpt } = opts
          const self = this
          const response = await this.$apollo.query({
            query: GET_VEHICLE_FILTER,
            variables: {
              filter: {
                status: ['I', 'R', 'A'],
                parentOnly: true,
                orderBy: orderBy ?? JSON.stringify({ licensePlate: 'asc' }),
                ...otherOpt,
              },
            },
          })
          let res = { ...response.data.vehiclesFilter.vehiclesFilterPaginated }
          res.data = res.data.map((item) => {
            let status = ''
            if (item.status === 'I') {
              status = self.$t('master.status.aktif')
            } else if (item.status === 'R') {
              status = self.$t('master.status.perbaikan')
            } else {
              status = self.$t('master.status.arsip')
            }
            return Object.assign({}, item, { statusString: status })
          })
          return res
        },
        users: async (opts) => {
          let result = {}

          const res = await this.$apollo.query({
            query: GET_EMPLOYEES_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...opts,
              },
            },
          })

          const employees = res.data.employeesPaginated.employeesPaginated

          let filteredEmployees = employees.data.map((item) => {
            return {
              ...item,
              id: item.userId,
            }
          })

          let ids = this.employeesAllData.map((item) => item.userId)

          if (opts.ugroupId) {
            ids = this.employeesAllData
              .filter((item) => item.groups.some((group) => group.id === opts.ugroupId))
              .map((item) => item.userId)
          }

          if (this.$store.state.identity.whoami.isBypass) {
            const currentUserId = this.$store.state.identity.whoami.userId
            filteredEmployees = filteredEmployees.filter((item) => item.id !== currentUserId)
            ids = ids.filter((id) => id !== currentUserId)
          }

          result = {
            data: filteredEmployees,
            ids,
            pagination: employees.pagination,
          }

          return result
        },

        durationOptions: (opts) => {
          let result = {}
          let durationOptions = this.durationOptions

          if (opts.search) {
            const search = opts.search.toLowerCase()
            durationOptions = durationOptions.filter((option) =>
              option.text.toLowerCase().includes(search)
            )
          }

          result = {
            metadata: {
              total_count: 5,
              page: 1,
            },
            data: {
              paginated_result: durationOptions,
            },
          }

          return result
        },
        vGroups: async (opts) => {
          let result = {}

          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
          }

          const res = await this.$apollo.query({
            query: GET_VGROUPS_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
              },
            },
          })

          let vgroups = res.data.vgroupsPaginated.vgroups

          result = {
            metadata: {
              total_count: vgroups.pagination.total,
              page: vgroups.pagination.page,
            },
            data: {
              paginated_result: vgroups.data,
            },
          }

          return result
        },
      }
    },

    dayOptions() {
      return [
        { value: 0, text: this.$t('global.days.sunday').slice(0, 3), disabled: false },
        { value: 1, text: this.$t('global.days.monday').slice(0, 3), disabled: false },
        { value: 2, text: this.$t('global.days.tuesday').slice(0, 3), disabled: false },
        { value: 3, text: this.$t('global.days.wednesday').slice(0, 3), disabled: false },
        { value: 4, text: this.$t('global.days.thursday').slice(0, 3), disabled: false },
        { value: 5, text: this.$t('global.days.friday').slice(0, 3), disabled: false },
        { value: 6, text: this.$t('global.days.saturday').slice(0, 3), disabled: false },
      ]
    },

    durationOptions() {
      return [
        { id: 0, value: 0, text: 'Instant' },
        { id: 10, value: 10, text: `10 ${this.$t('global.time.seconds')}` },
        { id: 20, value: 20, text: `20 ${this.$t('global.time.seconds')}` },
        { id: 30, value: 30, text: `30 ${this.$t('global.time.seconds')}` },
        { id: 40, value: 40, text: `40 ${this.$t('global.time.seconds')}` },
        { id: 50, value: 50, text: `50 ${this.$t('global.time.seconds')}` },
        { id: 60, value: 60, text: `60 ${this.$t('global.time.seconds')}` },
      ]
    },

    notificationFields() {
      return [
        {
          key: 'name',
          label: this.$t('setting.list.notification.form.notificationName'),
        },
        {
          label: this.$t('global.field.category'),
          key: 'alertCategories',
          formatter: (val) => {
            const key = this.lang == 'en' ? 'categoryEng' : 'categoryIdn'

            return val?.length > 0 ? val[0][key] : ''
          },
        },
        {
          key: 'description',
          label: this.$t('setting.list.notification.form.description'),
        },
      ]
    },

    validationMessages() {
      return {
        ruleName: {
          required: `${this.$t('setting.list.notification.form.ruleName')} ${this.$t(
            'global.validation.required'
          )}`,
        },
        notificationName: {
          required: `${this.$t('setting.list.notification.form.notificationName')} ${this.$t(
            'global.validation.required'
          )}`,
        },
        licensePlate: {
          required: `${this.$t('setting.list.notification.form.licensePlate')} ${this.$t(
            'global.validation.required'
          )}`,
        },
        geolocation: {
          required: `${this.$t('global.object.geolocation')} ${this.$t(
            'global.validation.required'
          )}`,
        },
        timeRange: {
          required: `${this.$t('global.time.range')} ${this.$t('global.validation.required')}`,
        },
        drivingTime: {
          required: `${this.$t('setting.list.notification.form.drivingTimeThreshold')} ${this.$t(
            'global.validation.required'
          )}`,
          drivingTimeRange: this.$t('setting.list.notification.form.drivingTimeRangeValidation'),
        },
        minimumRestTime: {
          required: `${this.$t('setting.list.notification.form.minimumRestTime')} ${this.$t(
            'global.validation.required'
          )}`,
          number: `${this.$t('setting.list.notification.form.minimumRestTime')} ${this.$t(
            'global.validation.number'
          )}`,
          min: this.$t('setting.list.notification.form.minimumRestTimeMinValidation'),
        },
        repeatNotificationInterval: {
          required: `${this.$t(
            'setting.list.notification.form.repeatNotificationInterval'
          )} ${this.$t('global.validation.required')}`,
          number: `${this.$t(
            'setting.list.notification.form.repeatNotificationInterval'
          )} ${this.$t('global.validation.number')}`,
          min: this.$t('setting.list.notification.form.repeatNotificationIntervalMinValidation'),
        },
      }
    },

    fieldGeofence() {
      return [
        {
          key: 'fenceName',
          label: this.$t('master.form.address.name'),
          isSortable: true,
        },
        {
          key: 'province',
          label: this.$t('master.list.address.province'),
          isSortable: true,
        },
        {
          key: 'city',
          label: this.$t('master.list.address.city'),
          isSortable: true,
        },
      ]
    },

    vehicleFields() {
      return [
        {
          label: this.$t('report.fields.licensePlate'),
          key: 'licensePlate',
          isSortable: true,
        },
        {
          label: this.$t('report.fields.vehicleType'),
          key: 'vehicleType',
          isSortable: false,
          formatter: (value) => {
            return value
              ? [value.manufacturer ?? '-', value.model ?? '-', value.type ?? '-'].join(' / ')
              : '-'
          },
        },
        {
          label: this.$t('report.fields.vehicleGroup'),
          key: 'vehicle2Vgroup',
          isSortable: false,
          formatter: (value) => {
            return value && value.length > 0 ? value.map((v) => v.vgroup.name).join(', ') : '-'
          },
        },
      ]
    },

    vehicleFilters() {
      return [
        {
          label: this.$t('report.fields.vehicleType'),
          key: 'vehicleType',
          data: this.vehicleTypes,
          model: [],
        },
        {
          label: this.$t('report.fields.vehicleGroup'),
          key: 'vgroup',
          data: this.$store.state.access.vGroups,
          model: [],
        },
      ]
    },

    userFields() {
      return [
        {
          label: this.$t('setting.list.notification.form.employeeName'),
          key: 'fullname',
          isSortable: false,
        },
        {
          label: 'Email',
          key: 'email',
          isSortable: false,
        },
        {
          label: this.$t('setting.list.notification.form.userGroup'),
          key: 'groups',
          isSortable: false,
          formatter: (val) => {
            return val.length > 0 ? val[0].name : '-'
          },
        },
      ]
    },

    vGroupFields() {
      return [
        {
          key: 'name',
          label: this.$t('global.object.fleetGroup'),
        },
        {
          key: 'totalVehicle',
          label: this.$t('global.object.totalFleet'),
        },
      ]
    },

    userFilters() {
      return [
        {
          label: this.$t('setting.list.notification.form.userGroup'),
          key: 'ugroupId',
          data: this.uGroups,
        },
      ]
    },

    queryGeofence() {
      return {
        get: async (opts) => {
          let result = {}

          const self = this
          self.globalSearch = opts.search

          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            orderBy: opts.sorts,
            search: opts.search,
            ownership: ['Customer', 'Own'],
          }

          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_ADDRESS,
            variables: {
              filter: filter,
            },
          })

          const data = res.data.geofences.geofences

          result = {
            metadata: {
              total_count: data.pagination.total,
              page: data.pagination.page,
            },
            data: {
              paginated_result: data.data,
            },
          }

          return result
        },
      }
    },
    tooltipContent() {
      return this.$t('setting.list.notification.form.intensityTooltip', {
        link: `<a href="https://kb.mceasy.com/docs/notifikasi-berulang-intensity-based-alerts/" target="_blank" class="tooltip-link">${this.$t(
          'setting.list.notification.form.learnMore'
        )}</a>`,
      }).replace(/\n/g, '<br>')
    },
    checkAlertCategory() {
      const alertCategoryFO = 6
      const alertCategoryId = this.fullObject.notification?.alertCategories?.map(
        (item) => item.alertCategoryId
      )
      if (Array.isArray(alertCategoryId)) {
        return alertCategoryId.includes(alertCategoryFO)
      }
      return false
    },
    showTooltipDuration() {
      return this.form.alertCode == 8 && this.form.duration == null
    },
    notifyIntervalItems() {
      const items = this.$t('setting.list.notification.drivingTimeTooltipItems')
      return `<ul class="mb-0 pl-3">${items.map((item) => `<li>${item}</li>`).join('')}</ul>`
    },
  },

  watch: {
    selectedDays: {
      handler(val) {
        if (val.length == 1) {
          this.dayOptions.forEach((day) => {
            if (val.includes(day.value)) {
              day.disabled = true
            } else {
              day.disabled = false
            }
          })
        } else {
          this.dayOptions.forEach((day) => {
            day.disabled = false
          })
        }
      },
    },
    '$i18n.locale': {
      handler(val) {
        this.lang = val
        if (this.$refs.formRef?.hasErrors && document.querySelector('.formulate-input-error')) {
          const submitButton = document.querySelector('button[type="submit"]')
          submitButton && submitButton.click()
        }
      },
    },
    checkAlertCategory: {
      handler(val) {
        if (val) {
          this.form = {
            ...this.form,
            showHideGeolocation: false,
            geofenceType: 'inside',
            insideGeofenceId: [],
            outsideGeofenceId: [],
            showHideTime: false,
            timeRange: [],
            speedLimit: null,
            showHideIntensity: false,
            vehicles: [],
            vehicleSelectionMode: 'allVehicles',
          }
          this.fullObject.licensePlate = {}
        }
      },
    },
    'form.alertCode': {
      handler(val) {
        if (val == 7) {
          this.form.geofenceType = 'inside'
        }
      },
    },
  },

  async mounted() {
    if (!this.$route.params.id) this.isAdding = true
    else {
      this.isLoading = true
      this.isAdding = false
      try {
        await this.getData()
      } finally {
        this.isLoading = false
      }
    }
  },

  methods: {
    drivingTimeRange({ value }) {
      if (!value || this.bypassDrivingTimeValidation) return true
      const minutes = this.timeToMinutes(value)
      return minutes >= 30 && minutes <= 720
    },

    async getData() {
      const res = await this.$apollo.query({
        fetchPolicy: 'no-cache',
        query: GET_NOTIFICATIONS_LIST_BY_ID,
        variables: {
          filter: {
            alertRuleId: Number(this.$route.params.id),
          },
        },
      })
      const data = res.data.alertRule.alertRule
      const isDrivingTime = Number(data.alertCode) === 117
      this.totalVehicles = data.totalVehicles
      this.totalVgroups = data.totalVgroups
      this.hasInaccessibleVehicle = data.hasInaccessibleVehicle
      this.hasInaccessibleVgroup = data.hasInaccessibleVgroup

      let speedLimit
      if (isDrivingTime) {
        speedLimit = null
      } else {
        speedLimit = data.maxThreshold
      }

      let drivingTime = '04:00'
      if (isDrivingTime && data.maxThreshold != null) {
        drivingTime = this.minutesToTime(data.maxThreshold)
      }

      let minimumRestTime = 30
      if (isDrivingTime && data.minThresholdTime != null) {
        minimumRestTime = Number(data.minThresholdTime)
        this.predefinedMinRestTime = Number(data.minThresholdTime)
      }

      let isRepeatNotification
      if (isDrivingTime) {
        isRepeatNotification = data?.alertFlag ? false : true
      } else {
        isRepeatNotification = false
      }

      let repeatNotificationInterval = 60
      if (isDrivingTime && data.repeatInterval != null) {
        repeatNotificationInterval = data.repeatInterval
      }

      this.form = {
        name: data.label,
        alertCode: data.alertCode,
        showHideGeolocation: data.withGeofence,
        geofenceType: data.isInsideGeofence ? 'inside' : 'outside',
        showHideTime: data.withSchedule,
        timeRange: data.withSchedule ? [data.startTime, data.endTime] : [],
        contactLists: data.contactRecipients,
        vehicles: data.vehicles.length > 0 ? data.vehicles.map((item) => item.id) : [],
        speedLimit,
        duration:
          data.minThresholdTime !== null
            ? this.durationOptions.find((item) => item.value === data.minThresholdTime)?.id ?? 0
            : null,
        alertCountThreshold: data.alertCountThreshold,
        alertCountWindow: data.alertCountWindow,
        showHideIntensity: !!(data.alertCountThreshold && data.alertCountWindow),
        userSettings: data.totalUsers > 0 ? 'selected' : 'all',
        users: data.totalUsers > 0 ? data.users.map((item) => item.userId) : [],
        urgencyLevel: data.alertUrgencyType,
        vehicleSelectionMode:
          data.totalVehicles > 0
            ? 'selectedVehicles'
            : data.totalVgroups > 0
            ? 'vehicleGroup'
            : 'allVehicles',
        autoPopup: data.autoPopup,
        geofences: data.geofences,
        vgroups: data.vgroups.map((item) => item.id),
        alertFlag: data.alertFlag,
        minimumRestTime,
        drivingTime,
        isRepeatNotification,
        repeatNotificationInterval,
      }

      if (data.alertCode == 7) this.form.geofenceType = 'inside'

      if (isDrivingTime) {
        this.alertSending = 'both'
      } else {
        this.alertSending = data.alertFlag ?? 'both'
      }

      this.fullObject = {
        notification: {
          ...data.alert,
          name: this.lang == 'en' ? data.alert.descriptionEng : data.alert.descriptionIdn,
          alertCategories: data.alert.alertCategories,
        },
        licensePlate: data.vehicles,
        users:
          data.totalUsers > 0
            ? data.users.map((user) => ({
                ...user,
                id: user.userId,
              }))
            : {},
        duration: {
          id: data.minThresholdTime ?? 0,
          value: data.minThresholdTime ?? 0,
          text:
            data.minThresholdTime !== null
              ? this.getDurationText(data.minThresholdTime) || 'Instant'
              : 'Instant',
        },
        vgroup: data.vgroups,
      }
      if (this.form.geofenceType == 'inside') {
        this.form.insideGeofenceId = data.geofences.map((item) => item.geofenceId)
        this.insideObj = data.geofences.filter((item) =>
          this.form.insideGeofenceId.includes(item.geofenceId)
        )
      } else {
        this.form.outsideGeofenceId = data.geofences.map((item) => item.geofenceId)
        this.outsideObj = data.geofences.filter((item) =>
          this.form.outsideGeofenceId.includes(item.geofenceId)
        )
      }
      this.selectedDays = data.dayOfWeek ?? []
    },

    setVehicleObj(value) {
      this.form.vehicleSelectionMode = value
      if (value == 'allVehicles') {
        this.form.vehicles = []
        this.fullObject.licensePlate = {}
        this.form.vgroups = []
        this.fullObject.vgroups = {}
      } else if (value == 'selectedVehicles') {
        this.form.vgroups = []
        this.fullObject.vgroups = {}
      } else if (value == 'vehicleGroup') {
        this.form.vehicles = []
        this.fullObject.licensePlate = {}
      }
    },

    setGeofenceObj(params) {
      if (params == 'inside') {
        this.form.outsideGeofenceId = []
        this.outsideObj = {}
      } else {
        this.form.insideGeofenceId = []
        this.insideObj = {}
      }
    },

    handleException(value) {
      if (value.exclude.length >= 0 && value.selectAll) {
        this.geofenceSelectionMode = 'Exclude'
      } else {
        this.geofenceSelectionMode = 'Selection'
      }
      this.exception = value
    },

    onChangeToggleGeofence(value) {
      this.form.showHideGeolocation = value
    },

    onChangeToggleIntensity(value) {
      this.form.showHideIntensity = value
      if (value) {
        this.form.alertCountThreshold = 3
        this.form.alertCountWindow = 20
      } else {
        this.form.alertCountThreshold = null
        this.form.alertCountWindow = null
      }
    },

    onChangeToggleTime(value) {
      this.form.showHideTime = value
    },

    onChangeAutoPopup(value) {
      this.form.autoPopup != value
    },

    setUserSettings(value) {
      this.form.userSettings = value

      const identity = this.$store.state.identity.whoami
      const isResettingToAll = value === 'all'
      const shouldResetUsers = isResettingToAll || identity.isBypass

      if (shouldResetUsers) {
        this.form.users = []
        this.fullObject.users = []
      } else {
        this.form.users = [identity.userId]
        this.fullObject.users = [
          {
            id: identity.userId,
            userId: identity.userId,
            fullname: identity.name,
          },
        ]
      }
    },

    addContact(value) {
      this.form.contactLists.push(value)
    },

    updateData(value) {
      this.form.contactLists = this.form.contactLists.map((item) => {
        if (item.contactId == value.updateContact.contactId) {
          return value.updateContact
        } else {
          return item
        }
      })
    },

    deleteData(value) {
      this.form.contactLists = this.form.contactLists.filter((item) => item.id !== value)
    },

    async handleFormSubmit(confirmOverride = false) {
      const formRef = this.$refs.formRef
      const hasError = await formRef.hasValidationErrors()
      if (hasError) {
        formRef.showErrors()
        return
      }

      const durationChanged =
        this.form.alertCode == 8 &&
        this.hasAccess(['e4d-alert-overspeed-threshold-duration:read']) &&
        Number(this.predefinedDuration) !== Number(this.form.duration)

      const minRestTimeChanged =
        this.hasDrivingTimeAccess &&
        Number(this.predefinedMinRestTime) !== Number(this.form.minimumRestTime)

      if (!confirmOverride && (durationChanged || minRestTimeChanged)) {
        this.overrideType = durationChanged ? 'overspeed' : 'drivingTime'
        this.$bvModal.show('modal-confirm-override')
        return
      }

      if (confirmOverride) this.$bvModal.hide('modal-confirm-override')
      this.$refs.formRef.formSubmitted()
    },

    async handleSubmit() {
      if (
        this.form.showHideTime &&
        (this.form.timeRange[0] == null || this.form.timeRange[1] == null)
      ) {
        this.$refs.formRef.showErrors()
        this.$mcToast.error('Please fill time range')
        return
      }

      if (
        (this.form.vehicleSelectionMode == 'selectedVehicles' && this.form.vehicles.length == 0) ||
        (this.form.vehicleSelectionMode == 'vehicleGroup' && this.form.vgroups.length == 0)
      ) {
        this.$refs.formRef.showErrors()
        return
      }

      const isDrivingTime = this.hasDrivingTimeAccess

      let alertFlag
      if (isDrivingTime) {
        alertFlag = !this.form.isRepeatNotification ? true : null
      } else if (this.alertSending === null || this.alertSending === 'both') {
        alertFlag = null
      } else {
        alertFlag = this.alertSending === true || this.alertSending === 'true'
      }

      let repeatInterval = null
      if (isDrivingTime) {
        repeatInterval = Number(this.form.repeatNotificationInterval)
      }

      let maxThreshold
      if (isDrivingTime) {
        maxThreshold = this.timeToMinutes(this.form.drivingTime)
      } else if (
        this.form.alertCode == 8 &&
        this.hasAccess(['e4c-alert-overspeed-threshold:read'])
      ) {
        maxThreshold = Number(this.form.speedLimit)
      } else {
        maxThreshold = undefined
      }

      let minThresholdTime
      if (isDrivingTime) {
        minThresholdTime = Number(this.form.minimumRestTime)
      } else if (
        this.form.alertCode == 8 &&
        this.hasAccess(['e4d-alert-overspeed-threshold-duration:read'])
      ) {
        minThresholdTime = this.form.duration
      }

      let input = {
        label: this.form.name,
        withGeofence: this.form.showHideGeolocation,
        geofence: {
          geofenceId:
            this.form.geofenceType == 'inside'
              ? this.form.insideGeofenceId
              : this.form.outsideGeofenceId,
          selectionMode: this.geofenceSelectionMode,
        },
        isInsideGeofence: this.form.geofenceType == 'inside' ? true : false,
        withSchedule: this.form.showHideTime,
        startTime: this.form.showHideTime ? this.form.timeRange[0] : null,
        endTime: this.form.showHideTime ? this.form.timeRange[1] : null,
        dayOfWeek: this.selectedDays,
        maxThreshold,
        alertCountThreshold: this.form.showHideIntensity
          ? Number(this.form.alertCountThreshold)
          : null,
        minThresholdTime,
        alertCountWindow: this.form.showHideIntensity ? this.form.alertCountWindow : null,
        userIds:
          this.form.userSettings === 'selected' ? this.form.users.map((id) => Number(id)) : [],
        alertUrgencyType: this.form.urgencyLevel,
        autoPopup: this.form.autoPopup,
        alertFlag,
        repeatInterval,
      }
      if (this.form.vehicleSelectionMode === 'allVehicles') {
        input.allVehicles = true
      } else if (this.form.vehicleSelectionMode === 'vehicleGroup') {
        input.vgroups =
          this.form.vgroups.length > 0 ? this.form.vgroups.map((id) => Number(id)) : []
      } else if (this.form.vehicleSelectionMode === 'selectedVehicles') {
        input.vehicles = this.form.vehicles.length > 0 ? this.form.vehicles : []
      }

      if (this.form.showHideGeolocation) {
        if (this.geofenceSelectionMode == 'Exclude') {
          const exclude = this.exception.exclude.map((item) => item.geofenceId)
          input.geofence.geofenceId = exclude
        } else {
          if (this.form.geofenceType == 'inside') {
            input.geofence.geofenceId = this.form.insideGeofenceId.map((id) => parseInt(id))
          } else {
            input.geofence.geofenceId = this.form.outsideGeofenceId.map((id) => parseInt(id))
          }
        }
      } else {
        input.geofence.geofenceId = []
        input.geofence.selectionMode = null
        input.withGeofence = false
      }
      if (this.isAdding) {
        const contacts = this.form.contactLists.map((item) => {
          return {
            email: item.email,
            label: item.label,
            phoneNumber: item.phoneNumber,
            userId: item.userId == 9999999 ? null : item.userId,
          }
        })
        const res = await this.$apollo.mutate({
          mutation: CREATE_NOTIFICATION_RULES,
          variables: {
            input: {
              ...input,
              contactRecipients: contacts,
              alertCode: Number(this.form.alertCode),
              entityType: 'AlertRule',
            },
          },
        })

        if (res.data.createAlertRule.isSuccessful) {
          this.$mcToast.success(this.$t('setting.list.notification.form.success'))
          localStorage.removeItem('alertRules')
          this.$router.push({
            name: 'Setting Notification List',
          })
        }
      } else {
        const res = await this.$apollo.mutate({
          mutation: UPDATE_NOTIFICATION_RULES,
          variables: {
            input: {
              ...input,
              alertRuleId: Number(this.$route.params.id),
            },
          },
        })

        if (res.data.updateAlertRule.isSuccessful) {
          this.$mcToast.success(this.$t('global.alert.text.saved'))
          localStorage.removeItem('alertRules')

          await this.$router.push({
            name: 'Setting Notification List',
          })
        } else {
          if (res.data.updateAlertRule.errorCode == 'E-AR1-U1/10')
            this.$mcToast.error(this.$t('notification.text.updateStatusFailed'))
          else this.$mcToast.error(res.data.updateAlertRule.message)
        }
      }
    },

    getDurationText(value) {
      const durationOptions = [
        { id: 0, value: 0, text: 'Instant' },
        { id: 10, value: 10, text: `10 ${this.$t('global.time.seconds')}` },
        { id: 20, value: 20, text: `20 ${this.$t('global.time.seconds')}` },
        { id: 30, value: 30, text: `30 ${this.$t('global.time.seconds')}` },
        { id: 40, value: 40, text: `40 ${this.$t('global.time.seconds')}` },
        { id: 50, value: 50, text: `50 ${this.$t('global.time.seconds')}` },
        { id: 60, value: 60, text: `60 ${this.$t('global.time.seconds')}` },
      ]
      return durationOptions.find((item) => item.value === value)?.text || 'Instant'
    },

    handleCancel() {
      this.$router.go(-1)
    },

    openChangeLog() {
      this.$refs.changelogModal.show()
    },
  },
}
</script>
